import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket } from 'lucide-react';
import { Button, cn } from '@scio3/ui';
import { PLAN_TEXT_FEATURES } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useSubscriptionModal } from '@/hooks/useSubscriptionModal';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Full-width upgrade banner shown on the Engage page when the user's plan
 * does not include the Engage feature. Brand green gradient + black Upgrade Now
 * CTA, matching the trial PromotionalBanner style.
 */
export function EngagePlanBanner({ force = false }: { force?: boolean }) {
  const { t } = useTranslation();
  const { engage } = useCompanyFeatures();
  const { isRootAdmin } = usePermissions();
  const company = useAuthStore((s) => s.company);
  const { openSubscriptionPlan } = useSubscriptionModal();

  const handleUpgrade = useCallback(() => {
    openSubscriptionPlan({
      title: t('Change Plan'),
      subtitle: t('Upgrade Required'),
      action: 'changePlanTrialToPay',
      sizeModal: 'xl',
      params: {
        planFeature: 'engage',
        minPlanRequired: 'engage',
        subtitle: t('Upgrade Required'),
        arrCssFile: true,
      },
    });
  }, [openSubscriptionPlan, t]);

  // Don't show if user already has Engage feature (unless forced for dev preview)
  if (engage && !force) return null;

  const currentPlan = PLAN_TEXT_FEATURES[company?.payment?.planFeature ?? ''] || '';

  return (
    <div
      className={cn(
        // Mint→primary gradient (deep teal in dark mode), dark text — matches the promotional banners.
        'hidden sm:flex min-h-12 w-full items-center justify-center gap-4 bg-gradient-to-l from-[#E8F7F4] to-[#98DDCF] px-6 py-2 text-sm text-[#313a46] dark:from-[#0B302C] dark:to-[#1B6F64] dark:text-white',
        isRootAdmin && 'cursor-pointer md:cursor-default',
      )}
      role="alert"
      onClick={isRootAdmin ? handleUpgrade : undefined}
    >
      <Rocket className="h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="font-semibold">
          {t('Engage features are only available with the Engage plan.')}
          {currentPlan && ' ' + t('Your current plan is {{plan}}.', { plan: currentPlan })}
        </span>
        <span className="hidden md:inline text-xs font-normal text-[#313a46] dark:text-white">
          {t('You can trial Engage features on 1 device with your plan.')}{' '}
          <a
            href="https://www.optisigns.com/pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#313a46] underline dark:text-white"
            onClick={(e) => e.stopPropagation()}
          >
            {t('Learn more')}
          </a>
        </span>
      </div>
      {isRootAdmin && (
        <Button
          size="sm"
          className="hidden md:inline-flex h-8 shrink-0 cursor-pointer rounded-md bg-[#313a46] px-5 text-xs font-semibold text-white transition-colors hover:bg-[#3d4856] dark:bg-slate-200 dark:text-[#313a46] dark:hover:bg-slate-300"
          onClick={(e) => {
            e.stopPropagation();
            handleUpgrade();
          }}
        >
          {t('Upgrade Now')}
        </Button>
      )}
    </div>
  );
}
